import { useState, useEffect, useCallback } from "react";
import { message } from "antd";
import { flightOperationTransactionService, flightOperationEventLogService } from "../../../../api/enhancedApi";
import { TRANSACTION_TYPES, TRANSACTION_API_ENDPOINTS, TRANSACTION_CHANGE_TYPES, TRANSACTION_STATUS, DEFAULT_TRANSACTION_DETAILS, TRANSACTION_TYPE_LABELS, TRANSACTION_TYPE_TO_MODEL_MAP } from "../constants";
import { createErrorHandler } from "../../../../utils/errorHandler";

/**
 * 自定义Hook: 获取和管理航班事务数据
 *
 * @param {number} flightId - 航班ID
 * @param {string} transactionType - 事务类型
 * @param {boolean} skipInitialFetch - 是否跳过初始数据获取（用于已有预加载数据的情况）
 * @returns {Object} 事务数据和操作方法
 */
const useFlightTransactions = (flightId, transactionType, skipInitialFetch = false) => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [eventLogs, setEventLogs] = useState([]);
    const [eventLogsLoading, setEventLogsLoading] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);

    // 检查是否为有效的事务类型
    const isValidType = Object.values(TRANSACTION_TYPES).includes(transactionType);

    // 创建专用的错误处理器
    const handleTransactionError = createErrorHandler(`${TRANSACTION_TYPE_LABELS[transactionType] || transactionType}操作`, {
        showNotification: true,
        duration: 5,
    });

    // 获取特定类型的事务列表
    const fetchTransactions = useCallback(async () => {
        if (!flightId || !isValidType) return;

        setLoading(true);
        setError(null);

        try {
            let response = null;

            // 根据事务类型调用不同的API方法
            switch (transactionType) {
                case TRANSACTION_TYPES.PERMIT_REQUEST:
                    response = await flightOperationTransactionService.getFlightPermitRequests(flightId);
                    break;
                case TRANSACTION_TYPES.FUEL_REQUEST:
                    response = await flightOperationTransactionService.getFlightFuelRequests(flightId);
                    break;
                case TRANSACTION_TYPES.CATERING_ORDER:
                    response = await flightOperationTransactionService.getFlightCateringOrders(flightId);
                    break;
                case TRANSACTION_TYPES.FLIGHT_BRIEF:
                    response = await flightOperationTransactionService.getFlightBriefs(flightId);
                    break;
                default:
                    setTransactions([]);
                    setLoading(false);
                    return;
            }

            if (response.data && Array.isArray(response.data)) {
                setTransactions(response.data);
            } else if (response.data && Array.isArray(response.data.items)) {
                // 处理分页响应格式 (如果后端API返回带分页的数据)
                setTransactions(response.data.items);
            } else {
                setTransactions([]);
            }

            setHasFetched(true);
        } catch (err) {
            console.error(`获取航班(ID=${flightId})${transactionType}事务失败:`, err);
            setError(err.message || "获取事务数据失败");
            handleTransactionError(err, { silent: false });
        } finally {
            setLoading(false);
        }
    }, [flightId, transactionType, isValidType]);

    // 获取事务相关的事件日志
    const fetchEventLogs = useCallback(async () => {
        if (!flightId) return;

        setEventLogsLoading(true);

        try {
            // 修改为只过滤航班ID和事务类型，不再使用transactionId过滤
            const response = await flightOperationEventLogService.getFlightEventLogsByFlightId(flightId, {
                related_business_type: TRANSACTION_TYPE_TO_MODEL_MAP[transactionType] || transactionType,
                limit: 50,
                sort_by: "created_at",
                sort_order: "desc",
            });

            if (response.data && Array.isArray(response.data.items)) {
                setEventLogs(response.data.items);
            } else {
                setEventLogs([]);
            }
        } catch (err) {
            console.error(`获取事务类型(${transactionType})相关事件日志失败:`, err);
            handleTransactionError(err, { silent: false });
        } finally {
            setEventLogsLoading(false);
        }
    }, [flightId, transactionType]);

    // 创建新事务
    const createTransaction = useCallback(
        async (data) => {
            if (!flightId || !isValidType) return null;

            try {
                const changeType = TRANSACTION_CHANGE_TYPES[transactionType].create;

                // 构建变更命令
                const command = {
                    flight_operation_id: flightId,
                    change_type: changeType,
                    payload: {
                        ...data,
                        details: data.details || DEFAULT_TRANSACTION_DETAILS[transactionType] || {},
                    },
                    operator: {
                        type: "USER",
                        id: "current_user", // 这里应该使用实际的用户ID
                    },
                    trigger_source: "MONITOR_UI",
                    remarks: data.remarks || "",
                };

                const response = await flightOperationTransactionService.createTransactionViaChange(command);

                // 正确处理 axios 响应结构 - 检查批量操作的详细结果
                if (response.data && response.data.success) {
                    // 检查是否有失败的项目
                    const responseData = response.data.data;
                    if (responseData && responseData.failed_items && responseData.failed_items.length > 0) {
                        // 有失败项目，从失败项目中查找当前航班
                        const failedItem = responseData.failed_items.find((item) => item.flight_id === flightId);
                        if (failedItem) {
                            const errorMessage = failedItem.error || "创建事务失败";
                            throw new Error(errorMessage);
                        }
                    }

                    // 操作成功
                    message.success(response.data.message || "创建事务成功");
                    return response.data.data; // 返回实际的操作结果数据
                } else {
                    // 获取错误消息，优先使用 response.data.message
                    const errorMessage = (response.data && response.data.message) || response.message || "创建事务失败";
                    throw new Error(errorMessage);
                }
            } catch (err) {
                console.error(`创建${transactionType}事务失败:`, err);
                handleTransactionError(err);
                return null;
            }
        },
        [flightId, transactionType, isValidType],
    );

    // 更新事务状态和详情
    const updateTransaction = useCallback(
        async (transactionId, data) => {
            if (!flightId || !transactionId || !isValidType) return false;

            try {
                const changeType = TRANSACTION_CHANGE_TYPES[transactionType].update;

                // 构建变更命令并添加正确的事务ID字段
                const payload = {
                    ...data,
                };

                // 根据不同事务类型设置正确的ID字段名称
                switch (transactionType) {
                    case TRANSACTION_TYPES.PERMIT_REQUEST:
                        payload.permit_request_id = transactionId;
                        break;
                    case TRANSACTION_TYPES.FUEL_REQUEST:
                        payload.fuel_request_id = transactionId;
                        break;
                    case TRANSACTION_TYPES.CATERING_ORDER:
                        payload.catering_order_id = transactionId;
                        break;
                    case TRANSACTION_TYPES.FLIGHT_BRIEF:
                        payload.flight_brief_id = transactionId;
                        break;
                    default:
                        payload.transaction_id = transactionId;
                }

                const command = {
                    flight_operation_id: flightId,
                    change_type: changeType,
                    payload: payload,
                    operator: {
                        type: "USER",
                        id: "current_user", // 这里应该使用实际的用户ID
                    },
                    trigger_source: "MONITOR_UI",
                    remarks: data.remarks || "",
                };

                const response = await flightOperationTransactionService.updateTransactionViaChange(command);

                // 正确处理 axios 响应结构 - 检查批量操作的详细结果
                if (response.data && response.data.success) {
                    // 检查是否有失败的项目
                    const responseData = response.data.data;
                    if (responseData && responseData.failed_items && responseData.failed_items.length > 0) {
                        // 有失败项目，从失败项目中查找当前航班
                        const failedItem = responseData.failed_items.find((item) => item.flight_id === flightId);
                        if (failedItem) {
                            const errorMessage = failedItem.error || "更新事务失败";
                            throw new Error(errorMessage);
                        }
                    }

                    // 操作成功
                    message.success(response.data.message || "更新事务成功");
                    return true; // 更新成功
                } else {
                    // 获取错误消息，优先使用 response.data.message
                    const errorMessage = (response.data && response.data.message) || response.message || "更新事务失败";
                    throw new Error(errorMessage);
                }
            } catch (err) {
                console.error(`更新事务(ID=${transactionId})失败:`, err);
                handleTransactionError(err);
                return false;
            }
        },
        [flightId, transactionType, isValidType],
    );

    // 批量创建多个航班的事务
    const batchCreateTransactions = useCallback(
        async (flightIds, data) => {
            if (!flightIds || !flightIds.length || !isValidType) return [];

            try {
                const changeType = TRANSACTION_CHANGE_TYPES[transactionType].create;
                const results = [];
                const failedResults = [];

                for (const id of flightIds) {
                    try {
                        // 构建变更命令
                        const command = {
                            flight_operation_id: id,
                            change_type: changeType,
                            payload: {
                                ...data,
                                details: data.details || DEFAULT_TRANSACTION_DETAILS[transactionType] || {},
                            },
                            operator: {
                                type: "USER",
                                id: "current_user", // 这里应该使用实际的用户ID
                            },
                            trigger_source: "MONITOR_UI_BATCH",
                            remarks: data.remarks || "",
                        };

                        const response = await flightOperationTransactionService.createTransactionViaChange(command);

                        // 正确处理 axios 响应结构 - 检查批量操作的详细结果
                        if (response.data && response.data.success) {
                            // 检查是否有失败的项目
                            const responseData = response.data.data;
                            if (responseData && responseData.failed_items && responseData.failed_items.length > 0) {
                                // 有失败项目，从失败项目中查找当前航班
                                const failedItem = responseData.failed_items.find((item) => item.flight_id === id);
                                if (failedItem) {
                                    failedResults.push({ flightId: id, success: false, error: failedItem.error });
                                } else {
                                    // 在成功项目中查找
                                    const successItem = responseData.success_items && responseData.success_items.find((item) => item.flight_id === id);
                                    if (successItem) {
                                        results.push({ flightId: id, success: true, data: successItem });
                                    } else {
                                        // 既不在成功也不在失败中，可能是数据结构问题
                                        failedResults.push({ flightId: id, success: false, error: "未知处理结果" });
                                    }
                                }
                            } else {
                                // 没有失败项目，认为成功
                                results.push({ flightId: id, success: true, data: response.data.data });
                            }
                        } else {
                            const errorMessage = (response.data && response.data.message) || response.message || "创建失败";
                            failedResults.push({ flightId: id, success: false, error: errorMessage });
                        }
                    } catch (err) {
                        // 确保使用 err.message
                        failedResults.push({ flightId: id, success: false, error: err.message || "创建时发生未知错误" });
                    }
                }

                // 报告结果
                if (results.length > 0) {
                    message.success(`成功为${results.length}个航班创建${TRANSACTION_TYPE_LABELS[transactionType]}`);
                }

                if (failedResults.length > 0) {
                    message.warning(`${failedResults.length}个航班创建${TRANSACTION_TYPE_LABELS[transactionType]}失败，请检查详情`);
                }

                // 刷新当前航班的事务列表(如果当前航班在操作列表中)
                if (flightId && flightIds.includes(flightId)) {
                    await fetchTransactions();
                }

                return { success: results, failed: failedResults };
            } catch (err) {
                console.error(`批量创建${transactionType}事务失败:`, err);
                message.error(`批量创建事务失败: ${err.message || "未知错误"}`);
                return { success: [], failed: flightIds.map((id) => ({ flightId: id, success: false, error: err.message })) };
            }
        },
        [transactionType, isValidType, fetchTransactions, flightId],
    );

    // 批量更新多个航班的事务
    const batchUpdateTransactions = useCallback(
        async (transactions, data) => {
            if (!transactions || !transactions.length || !isValidType) return [];

            try {
                const changeType = TRANSACTION_CHANGE_TYPES[transactionType].update;
                const results = [];
                const failedResults = [];

                for (const item of transactions) {
                    try {
                        // 构建变更命令及正确的事务ID字段
                        const payload = {
                            ...data,
                        };

                        // 根据不同事务类型设置正确的ID字段名称
                        switch (transactionType) {
                            case TRANSACTION_TYPES.PERMIT_REQUEST:
                                payload.permit_request_id = item.transactionId;
                                break;
                            case TRANSACTION_TYPES.FUEL_REQUEST:
                                payload.fuel_request_id = item.transactionId;
                                break;
                            case TRANSACTION_TYPES.CATERING_ORDER:
                                payload.catering_order_id = item.transactionId;
                                break;
                            case TRANSACTION_TYPES.FLIGHT_BRIEF:
                                payload.flight_brief_id = item.transactionId;
                                break;
                            default:
                                payload.transaction_id = item.transactionId;
                        }

                        const command = {
                            flight_operation_id: item.flightId,
                            change_type: changeType,
                            payload: payload,
                            operator: {
                                type: "USER",
                                id: "current_user", // 这里应该使用实际的用户ID
                            },
                            trigger_source: "MONITOR_UI_BATCH",
                            remarks: data.remarks || "",
                        };

                        const response = await flightOperationTransactionService.updateTransactionViaChange(command);

                        // 正确处理 axios 响应结构 - 检查批量操作的详细结果
                        if (response.data && response.data.success) {
                            // 检查是否有失败的项目
                            const responseData = response.data.data;
                            if (responseData && responseData.failed_items && responseData.failed_items.length > 0) {
                                // 有失败项目，从失败项目中查找当前航班
                                const failedItem = responseData.failed_items.find((failedItemData) => failedItemData.flight_id === item.flightId);
                                if (failedItem) {
                                    failedResults.push({
                                        flightId: item.flightId,
                                        transactionId: item.transactionId,
                                        success: false,
                                        error: failedItem.error,
                                    });
                                } else {
                                    // 在成功项目中查找
                                    const successItem = responseData.success_items && responseData.success_items.find((successItemData) => successItemData.flight_id === item.flightId);
                                    if (successItem) {
                                        results.push({
                                            flightId: item.flightId,
                                            transactionId: item.transactionId,
                                            success: true,
                                            data: successItem,
                                        });
                                    } else {
                                        // 既不在成功也不在失败中，可能是数据结构问题
                                        failedResults.push({
                                            flightId: item.flightId,
                                            transactionId: item.transactionId,
                                            success: false,
                                            error: "未知处理结果",
                                        });
                                    }
                                }
                            } else {
                                // 没有失败项目，认为成功
                                results.push({
                                    flightId: item.flightId,
                                    transactionId: item.transactionId,
                                    success: true,
                                    data: response.data.data,
                                });
                            }
                        } else {
                            const errorMessage = (response.data && response.data.message) || response.message || "更新失败";
                            failedResults.push({
                                flightId: item.flightId,
                                transactionId: item.transactionId,
                                success: false,
                                error: errorMessage,
                            });
                        }
                    } catch (err) {
                        // 确保使用 err.message
                        failedResults.push({
                            flightId: item.flightId,
                            transactionId: item.transactionId,
                            success: false,
                            error: err.message || "更新时发生未知错误",
                        });
                    }
                }

                // 报告结果
                if (results.length > 0) {
                    message.success(`成功更新${results.length}个${TRANSACTION_TYPE_LABELS[transactionType]}`);
                }

                if (failedResults.length > 0) {
                    message.warning(`${failedResults.length}个${TRANSACTION_TYPE_LABELS[transactionType]}更新失败，请检查详情`);
                }

                // 刷新当前航班的事务列表(如果当前航班在操作列表中)
                if (flightId && transactions.some((item) => item.flightId === flightId)) {
                    await fetchTransactions();
                }

                return { success: results, failed: failedResults };
            } catch (err) {
                console.error(`批量更新${transactionType}事务失败:`, err);
                message.error(`批量更新事务失败: ${err.message || "未知错误"}`);
                return {
                    success: [],
                    failed: transactions.map((item) => ({
                        flightId: item.flightId,
                        transactionId: item.transactionId,
                        success: false,
                        error: err.message,
                    })),
                };
            }
        },
        [transactionType, isValidType, fetchTransactions, flightId],
    );

    // 只在组件挂载时获取事务数据，并且只有在未提供skipInitialFetch标志时才发起请求
    useEffect(() => {
        if (!skipInitialFetch && !hasFetched) {
            fetchTransactions();
        }
    }, [fetchTransactions, skipInitialFetch, hasFetched]);

    return {
        transactions,
        loading,
        error,
        eventLogs,
        eventLogsLoading,
        fetchTransactions,
        fetchEventLogs,
        createTransaction,
        updateTransaction,
        batchCreateTransactions,
        batchUpdateTransactions,
    };
};

export default useFlightTransactions;
